import React from 'react';
import { Icon, LOG_ACTION_ICONS } from './Icons';

const API_BASE = import.meta.env.VITE_APP_API_URL || 'http://localhost:3001/api';

export const resolveMediaUrl = (path) => {
  if (!path) return null;
  return path.startsWith('http') ? path : `${API_BASE}/membership${path}`;
};

export const displayName = (title, name) => {
  const n = (name || '').trim();
  if (!title) return n;
  return n.startsWith(`${title} `) || n.startsWith(`${title}. `) ? n : `${title} ${n}`.trim();
};

export const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value).split('T')[0] : d.toLocaleDateString();
};

export const Avatar = ({ src, name = '', size = 'w-14 h-14' }) => {
  const url = resolveMediaUrl(src);
  // Pictures uploaded before uploads were kept in file-service are gone; show initials instead
  const [failedUrl, setFailedUrl] = React.useState(null);
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  if (url && url !== failedUrl) {
    return (
      <div className={`${size} shrink-0 overflow-hidden rounded-full border border-gray-200 bg-gray-100`}>
        <img src={url} alt="Profile" className="h-full w-full object-cover" onError={() => setFailedUrl(url)} />
      </div>
    );
  }
  return (
    <div className={`${size} flex shrink-0 items-center justify-center rounded-full bg-violet-50 text-violet-700 text-base font-medium`}>
      {initials || <Icon name="user" className="w-6 h-6" />}
    </div>
  );
};

export const ProfileSummary = ({ picture, name, subtitle, children }) => (
  <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:flex-row sm:items-center sm:justify-between">
    <div className="flex min-w-0 items-center gap-4">
      <Avatar src={picture} name={name} />
      <div className="min-w-0">
        <h2 className="truncate text-lg font-medium text-gray-900">{name}</h2>
        {subtitle && <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>}
      </div>
    </div>
    {children && <div className="flex flex-wrap items-center gap-6">{children}</div>}
  </div>
);

export const SummaryStat = ({ label, value }) => (
  <div className="sm:text-right">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="mt-0.5 text-sm font-medium text-gray-900">{value || 'N/A'}</p>
  </div>
);

export const DetailGrid = ({ items, columns = 2 }) => (
  <dl className={`grid grid-cols-1 gap-x-8 gap-y-5 ${columns === 2 ? 'sm:grid-cols-2' : ''}`}>
    {items.map(([label, value]) => (
      <div key={label} className="min-w-0">
        <dt className="text-xs text-gray-500">{label}</dt>
        <dd className="mt-1 text-sm font-medium text-gray-900 break-words">
          {value || <span className="text-gray-400 font-normal">N/A</span>}
        </dd>
      </div>
    ))}
  </dl>
);

const ACTION_META = {
  created: { label: 'Created', tone: 'bg-blue-50 text-blue-600' },
  edited: { label: 'Edited', tone: 'bg-amber-50 text-amber-600' },
  approved_1st_level: { label: 'Approved at first level', tone: 'bg-indigo-50 text-indigo-600' },
  approved_committee: { label: 'Approved at committee level', tone: 'bg-purple-50 text-purple-600' },
  payment_approved: { label: 'Payment approved', tone: 'bg-emerald-50 text-emerald-600' },
  bill_printed: { label: 'Bill printed', tone: 'bg-cyan-50 text-cyan-600' },
  rejected: { label: 'Rejected', tone: 'bg-rose-50 text-rose-600' },
  requested_info: { label: 'Information requested', tone: 'bg-rose-50 text-rose-600' },
  removal_requested: { label: 'Removal requested', tone: 'bg-rose-50 text-rose-600' },
  status_changed: { label: 'Status changed', tone: 'bg-orange-50 text-orange-600' },
  submitted: { label: 'Submitted by applicant', tone: 'bg-emerald-50 text-emerald-600' },
};

export const ActivityTimeline = ({ entries, fallback, labels = {} }) => {
  const list = entries && entries.length > 0 ? entries : (fallback ? [fallback] : []);
  if (list.length === 0) return <p className="text-sm text-gray-500">No activity recorded.</p>;
  return (
    <ol>
      {list.map((log, i) => {
        const meta = ACTION_META[log.action] || { label: log.action || 'Event logged', tone: 'bg-gray-100 text-gray-600' };
        const label = labels[log.action] || meta.label;
        const isLast = i === list.length - 1;
        return (
          <li key={i} className={`relative flex gap-3 ${isLast ? '' : 'pb-6'}`}>
            {!isLast && <span className="absolute left-4 top-8 bottom-0 -ml-px w-px bg-gray-200" aria-hidden="true" />}
            <span className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${meta.tone}`}>
              <Icon name={LOG_ACTION_ICONS[log.action] || 'clock'} className="w-4 h-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-medium text-gray-900">{label}</p>
                <time className="shrink-0 text-xs text-gray-500">{formatDate(log.timestamp)}</time>
              </div>
              <p className="mt-0.5 text-xs text-gray-500">
                By {log.performedByName || 'System'}
                {log.performedByRole && <span className="ml-1.5 rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">{log.performedByRole}</span>}
              </p>
              {log.notes && (
                <p className="mt-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-600">{log.notes}</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export const DocumentRow = ({ name, meta, onView }) => (
  <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3">
    <div className="flex min-w-0 items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
        <Icon name="document" className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-gray-900">{name}</p>
        {meta && <p className="text-xs text-gray-500">{meta}</p>}
      </div>
    </div>
    <button
      type="button"
      onClick={onView}
      className="shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
    >
      View
    </button>
  </div>
);
